/*
  printPdf — tiny helper to send a PDF straight to the browser's print
  dialog without forcing the user to download → open → print.

  Two entry points:
    * printPdfBlob(blob)         — for blobs already in memory (e.g.
                                    letters/intake PDFs fetched via
                                    authenticated axios).
    * printPdfFromUrl(url)       — for endpoints we can call with
                                    credentials from fetch(). Works
                                    for same-origin URLs (our /api
                                    routes proxied through the same
                                    host as the frontend).

  Both funnel through _printBlob which:
    1. Creates a hidden iframe pointing at an object URL.
    2. Waits for load, focuses the iframe, calls `print()`.
    3. Cleans up the iframe + object URL after the print dialog
       closes (or after a 60 s safety timeout).

  We prefer the blob/iframe route over `window.open` because pop-up
  blockers routinely eat auto-print pop-ups, and blob URLs are same-
  origin so `iframe.contentWindow.print()` never trips CORS.
*/

function _printBlob(blob) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.setAttribute("aria-hidden", "true");
    iframe.src = url;

    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      try { iframe.remove(); } catch { /* ignore */ }
      try { URL.revokeObjectURL(url); } catch { /* ignore */ }
    };

    iframe.onload = () => {
      try {
        const win = iframe.contentWindow;
        win.focus();
        // A short timeout gives the embedded PDF viewer a moment to
        // render before we pop the print dialog. Without this, some
        // Chromium builds print a blank first page.
        setTimeout(() => {
          try {
            win.print();
            resolve();
          } catch (e) {
            reject(e);
            cleanup();
            return;
          }
          // Some browsers block until the print dialog is dismissed
          // (Chrome); others don't (Firefox). Either way, remove the
          // iframe after a generous delay so a cancelled print still
          // gets cleaned up.
          setTimeout(cleanup, 60_000);
          // Also cleanup on focus return (fires after the dialog
          // closes on most browsers).
          const onFocus = () => {
            window.removeEventListener("focus", onFocus);
            setTimeout(cleanup, 1_000);
          };
          window.addEventListener("focus", onFocus);
        }, 200);
      } catch (e) {
        reject(e);
        cleanup();
      }
    };
    iframe.onerror = (e) => {
      reject(e);
      cleanup();
    };
    document.body.appendChild(iframe);
  });
}

export async function printPdfBlob(blob) {
  if (!blob) throw new Error("printPdfBlob: blob required");
  return _printBlob(blob);
}

export async function printPdfFromUrl(url, { credentials = "include" } = {}) {
  const r = await fetch(url, { credentials });
  if (!r.ok) {
    const detail = await r.text().catch(() => "");
    throw new Error(detail || `Print failed (${r.status})`);
  }
  const blob = await r.blob();
  return _printBlob(blob);
}
